import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Bell, BellOff } from 'lucide-react';
import Tabs from '@/ui/tabs/Tabs';
import useStore from '@/store/store';
import { useCommStore } from './commStore';
import { commApi } from './commApi';
import { Inbox } from './Inbox';
import { CommSettings } from './CommSettings';
import { CommPermissions } from './CommPermissions';
import { CommAudit } from './CommAudit';

// Each user's own switch for message notifications (push and browser).
export const NotificationToggle = () => {
	const me = useCommStore(s => s.me);
	const setMe = useCommStore(s => s.setMe);
	if (!me) return null;
	const on = me.notificationsEnabled;
	const toggle = async () => {
		if (!on && typeof Notification !== 'undefined' && Notification.permission === 'default') {
			try {
				await Notification.requestPermission();
			} catch {
				/* ignore */
			}
		}
		const res = await commApi.setPrefs(!on);
		if (!res?.error) setMe({ ...me, notificationsEnabled: res.notificationsEnabled });
	};
	return (
		<button
			type='button'
			onClick={toggle}
			className='flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-[13px] text-slate-700'
			title='Ειδοποιήσεις για νέα μηνύματα'
		>
			{on ? (
				<Bell size={15} className='text-[#0f9b9d]' />
			) : (
				<BellOff size={15} className='text-slate-400' />
			)}
			Ειδοποιήσεις {on ? 'ON' : 'OFF'}
		</button>
	);
};

// Super Admin filters on top of the inbox: city, role, user, unread.
const SuperFilters = ({ cities, value, onChange }) => (
	<div className='mb-2 flex flex-wrap items-center gap-2 text-[13px]'>
		<select
			className='h-9 rounded-md border border-slate-300 px-2'
			value={value.cityId}
			onChange={e => onChange({ ...value, cityId: e.target.value })}
		>
			<option value=''>Όλες οι πόλεις</option>
			{(cities || []).map(c => (
				<option key={c.city_id} value={c.city_id}>
					{c.name}
				</option>
			))}
		</select>
		<select
			className='h-9 rounded-md border border-slate-300 px-2'
			value={value.role}
			onChange={e => onChange({ ...value, role: e.target.value })}
		>
			<option value=''>Όλοι οι ρόλοι</option>
			<option value='shop'>Καταστήματα</option>
			<option value='delivery'>Διανομείς</option>
			<option value='admin'>Διαχείριση</option>
			<option value='order'>Παραγγελίες</option>
		</select>
		<input
			className='h-9 w-28 rounded-md border border-slate-300 px-2'
			placeholder='User ID'
			inputMode='numeric'
			value={value.userId}
			onChange={e => onChange({ ...value, userId: e.target.value })}
		/>
		<label className='flex items-center gap-1'>
			<input
				type='checkbox'
				className='accent-[#0f9b9d]'
				checked={value.unread}
				onChange={e => onChange({ ...value, unread: e.target.checked })}
			/>
			Μόνο αδιάβαστα
		</label>
	</div>
);

// Μηνύματα: the admin's Inbox / Καταστήματα / Διανομείς, and for the Super
// Admin the Communication Center of every city plus Settings, Permissions and
// the Audit Log. A city admin gets Settings / Audit only when allowed.
const CommunicationCenter = () => {
	const { user } = useStore();
	const me = useCommStore(s => s.me);
	const [params] = useSearchParams();
	const initialId = Number(params.get('c')) || null;
	const [cities, setCities] = useState(null);
	// Starts on the city chosen in the header; "Όλες οι πόλεις" (or another
	// city) only when the Super Admin picks it.
	const [superFilters, setSuperFilters] = useState({
		cityId: user?.role === 'master' && user?.cityLocation?.id ? String(user.cityLocation.id) : '',
		role: '',
		userId: '',
		unread: false,
	});

	const isSuper = user?.role === 'master';
	const cityId = isSuper ? user?.cityLocation?.id : undefined;

	useEffect(() => {
		if (isSuper || me?.canToggleChat || me?.canViewAudit) {
			commApi.settings().then(res => setCities(res?.cities || []));
		}
	}, [isSuper, me?.canToggleChat, me?.canViewAudit]);

	if (!me) return <div className='p-6 text-slate-400'>Φόρτωση…</div>;
	if (!me.canUse) {
		return (
			<div className='p-6 text-slate-500'>
				Τα μηνύματα δεν είναι ενεργά για εσάς.
				{me.canToggleChat && (
					<div className='mt-4 max-w-xl'>
						<CommSettings isSuperAdmin={false} />
					</div>
				)}
			</div>
		);
	}

	const inbox = (fixed, extra) => (
		<Inbox
			key={initialId || 'inbox'}
			fixed={fixed}
			extraFilters={extra}
			cityId={cityId}
			initialId={initialId}
			showCity={isSuper}
		/>
	);

	const tabs = isSuper
		? [
				{
					label: 'Συνομιλίες',
					content: (
						<>
							<SuperFilters cities={cities} value={superFilters} onChange={setSuperFilters} />
							{inbox(
								{},
								{
									cityId: superFilters.cityId || undefined,
									role: superFilters.role || undefined,
									userId: superFilters.userId || undefined,
									unread: superFilters.unread ? 1 : undefined,
								},
							)}
						</>
					),
				},
				{ label: 'Ρυθμίσεις', content: <CommSettings isSuperAdmin /> },
				{ label: 'Δικαιώματα', content: <CommPermissions cities={cities} /> },
				{ label: 'Audit Log', content: <CommAudit cities={cities} isSuperAdmin /> },
			]
		: [
				{ label: 'Inbox', content: inbox({}) },
				...(user?.role === 'admin'
					? [
							{ label: 'Καταστήματα', content: inbox({ role: 'shop' }) },
							{ label: 'Διανομείς', content: inbox({ role: 'delivery' }) },
						]
					: []),
				...(me.canToggleChat
					? [{ label: 'Ρυθμίσεις', content: <CommSettings isSuperAdmin={false} /> }]
					: []),
				...(me.canViewAudit
					? [{ label: 'Audit Log', content: <CommAudit cities={cities} /> }]
					: []),
			];

	return (
		<div className='flex flex-col gap-2 px-3 pb-4'>
			<div className='flex items-center justify-end'>
				<NotificationToggle />
			</div>
			<Tabs tabItems={tabs} />
		</div>
	);
};

export default CommunicationCenter;
